import { useEffect, useRef } from 'react';

import { appBorders } from '@backrs_ui/common-ui';

import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getSessionState,
  getActiveLubavSession,
  createSessionAction,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import {
  setInputBoxMessage,
  setUserInput,
} from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession';

import { SxProps, Theme, Box, IconButton, InputAdornment } from '@mui/material';
import TextareaAutosize, { TextareaAutosizeProps } from 'react-textarea-autosize';

type InputBoxProps = Readonly<{
  sendQAMessage: (message: string, inputBoxMessage: string) => void;
  sendChatMessage: (message: string) => void;
  isActive?: boolean;
  isNumeric?: boolean;
  placeholder?: string;
}>;

export const InputBox = ({
  sendQAMessage,
  sendChatMessage,
  isActive = false,
  isNumeric = false,
  placeholder,
}: InputBoxProps) => {
  const activeSession = useAppSelector(getActiveLubavSession);

  const { inputBoxMessage, chatMode, userInput } = useAppSelector(
    (state) => getSessionState(state, activeSession).solutionSession,
  );

  const dispatch = useAppDispatch();

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isActive || chatMode) {
      textareaRef.current?.focus();
    }
  }, [isActive, chatMode]);

  if (!activeSession) {
    return null;
  }

  return (
    <Box sx={{ ...containerStyle, ...(isActive ? appBorders.backrsDropShadowBorders.blue : null) }}>
      <Box sx={boxStyle}>
        <TextareaAutosize
          ref={textareaRef}
          value={inputBoxMessage}
          onChange={(event) => {
            const value = event.target.value;

            if (isNumeric && isNaN(Number(value))) return;

            dispatch(
              createSessionAction(setUserInput, activeSession)({ type: 'SINGLE_INPUT', value }),
            );
            dispatch(createSessionAction(setInputBoxMessage, activeSession)(value));
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && (chatMode || isActive)) {
              e.preventDefault();
              if (!inputBoxMessage.trim() || userInput?.type !== 'SINGLE_INPUT') return;

              if (chatMode) {
                sendChatMessage(userInput.value);
              } else {
                sendQAMessage(userInput.value, inputBoxMessage);
              }

              dispatch(createSessionAction(setInputBoxMessage, activeSession)(''));
              dispatch(createSessionAction(setUserInput, activeSession)(undefined));
            }
          }}
          placeholder={
            !(chatMode || isActive)
              ? ''
              : (placeholder ?? (chatMode ? 'Keep the conversation going!' : 'Type a message...'))
          }
          disabled={!(chatMode || isActive)}
          style={{
            ...messageInputTextarea,
            color: chatMode || isActive ? 'inherit' : '#a0a0a0',
          }}
          minRows={1}
          maxRows={Math.floor((window.innerHeight * 0.2) / 24)}
        />
        <InputAdornment position="end" sx={inputAdornmentStyle}>
          <IconButton
            onClick={() => {
              if (!inputBoxMessage.trim() || userInput?.type !== 'SINGLE_INPUT') return;

              if (chatMode) {
                sendChatMessage(userInput.value);
              } else {
                sendQAMessage(userInput.value, inputBoxMessage);
              }

              dispatch(createSessionAction(setInputBoxMessage, activeSession)(''));
              dispatch(createSessionAction(setUserInput, activeSession)(undefined));
            }}
            disabled={!(chatMode || isActive)}
          >
            <img
              src="/send-message-icon.svg"
              alt="Send Message"
              style={{
                ...sendMessageIcon,
                opacity: chatMode || isActive ? '100%' : '50%',
                filter: chatMode || isActive ? 'none' : 'grayscale(100%)',
              }}
            />
          </IconButton>
        </InputAdornment>
      </Box>
    </Box>
  );
};

const containerStyle: SxProps<Theme> = {
  width: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column-reverse',
  alignItems: 'center',
  borderRadius: '8px',
  ...appBorders.backrsDropShadowBorders.black,
  '@media (max-width: 900px)': {
    width: 'calc(100vw - 170px)',
  },
};

const boxStyle: SxProps<Theme> = {
  display: 'grid',
  width: '100%',
  gridTemplateColumns: '1fr auto',
  alignItems: 'flex-end',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
};

const messageInputTextarea: TextareaAutosizeProps['style'] = {
  width: '100%',
  resize: 'none',
  padding: '20px 10px',
  marginLeft: '10px',
  border: 'none',
  outline: 'none',
  fontFamily: 'inherit',
  fontSize: '12pt',
  overflowY: 'auto',
  lineHeight: '1.25',
  display: 'flex',
  alignItems: 'center',
};

const inputAdornmentStyle: SxProps<Theme> = {
  height: 'auto',
  mr: 0.5,
  mb: 1.6,
  ml: 2,
};

const sendMessageIcon: React.CSSProperties = {
  width: '35px',
  height: '35px',
} as const;
